import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { DM_TYPES, LEVELS } from "../lib/s1000dData";
import { useEstimate } from "../store/EstimateContext";
import { fmtHours } from "../lib/calculations";
import MatrixImporter from "./MatrixImporter";
import HelpIconButton from "./HelpIconButton";

export default function DataModulesTab() {
  const { t } = useTranslation();
  const { estimate, totals, updateDmCell } = useEstimate();

  return (
    <section className="space-y-4" data-testid="data-modules-tab">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("modules.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("modules.title")}
            <HelpIconButton anchor="ch2" testId="help-icon-modules" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("modules.description")}{" "}
            <span className="font-mono text-xs">{t("modules.complexitySimple")}</span> ·{" "}
            <span className="font-mono text-xs">{t("modules.complexityIntermediate")}</span> ·{" "}
            <span className="font-mono text-xs">{t("modules.complexityComplex")}</span>.
          </p>
        </div>

        <div className="border border-gray-300 px-4 py-3 bg-white min-w-[180px]" data-testid="dm-hours-badge">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("modules.dmHours")}
          </p>
          <p className="font-mono text-2xl font-bold text-black leading-none mt-1">
            {fmtHours(totals.dmHours)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
            {totals.totalDms} {t("units.modulesTotal")}
          </p>
        </div>
      </div>

      <MatrixImporter target="dm" testidPrefix="dm" />

      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="dm-matrix-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3 sticky left-0 bg-gray-100 border-r border-gray-200">{t("tableHeaders.dmType")}</th>
              {LEVELS.map((lvl) => (
                <th key={lvl.key} colSpan={2} className="py-3 px-3 border-l border-gray-200">
                  <div>{t(`levels.${lvl.key}`)}</div>
                  <div className="text-[9px] font-normal text-gray-500 normal-case tracking-normal">
                    {t(`levels.${lvl.key}Subtitle`)} · {t("levels.defaultHrs", { n: lvl.defaultHours })}
                  </div>
                </th>
              ))}
              <th className="py-3 px-3 border-l border-gray-200 text-right">{t("tableHeaders.typeTotalHrs")}</th>
            </tr>
            <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-200">
              <th className="py-1.5 px-3 sticky left-0 bg-gray-50 border-r border-gray-200"></th>
              {LEVELS.map((lvl) => (
                <Fragment key={lvl.key}>
                  <th className="py-1.5 px-3 font-normal border-l border-gray-200">{t("tableHeaders.qty")}</th>
                  <th className="py-1.5 px-3 font-normal">{t("tableHeaders.hrsUnit")}</th>
                </Fragment>
              ))}
              <th className="py-1.5 px-3 border-l border-gray-200"></th>
            </tr>
          </thead>
          <tbody>
            {DM_TYPES.map((t) => {
              const row = estimate.dmMatrix[t.code] || {};
              return (
                <tr key={t.code} className="border-b border-gray-200 hover:bg-[#F9FAFB]">
                  <td className="py-2 px-3 sticky left-0 bg-white border-r border-gray-200">
                    <div className="font-bold text-black">{t.name}</div>
                    <div className="font-mono text-[10px] text-gray-500">{t.code}</div>
                  </td>
                  {LEVELS.map((lvl) => {
                    const cell = row[lvl.key] || { qty: 0, hours: lvl.defaultHours };
                    return (
                      <Fragment key={`${t.code}-${lvl.key}`}>
                        <td className="py-1.5 px-2 border-l border-gray-200">
                          <input
                            type="number"
                            min="0"
                            value={cell.qty}
                            onChange={(e) => updateDmCell(t.code, lvl.key, "qty", e.target.value)}
                            data-testid={`dm-${t.code}-${lvl.key}-qty`}
                            className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-20 text-right"
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={cell.hours}
                            onChange={(e) => updateDmCell(t.code, lvl.key, "hours", e.target.value)}
                            data-testid={`dm-${t.code}-${lvl.key}-hours`}
                            className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-20 text-right"
                          />
                        </td>
                      </Fragment>
                    );
                  })}
                  <td
                    className="py-2 px-3 border-l border-gray-200 font-mono text-sm text-right text-black"
                    data-testid={`dm-${t.code}-total`}
                  >
                    {fmtHours(totals.perTypeHours[t.code] || 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-3 px-3 sticky left-0 bg-black">{t("tableHeaders.levelSubtotals")}</td>
              {LEVELS.map((lvl) => (
                <td
                  key={lvl.key}
                  colSpan={2}
                  className="py-3 px-3 font-mono text-right text-sm normal-case tracking-tight"
                  data-testid={`dm-level-total-${lvl.key}`}
                >
                  {fmtHours(totals.perLevelHours[lvl.key])} hrs
                </td>
              ))}
              <td
                className="py-3 px-3 font-mono text-right text-sm normal-case"
                data-testid="dm-grand-total"
              >
                {fmtHours(totals.dmHours)} hrs
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
